import { type ModifierGroupVariation } from '@sg/graphql-schema';

import {
  type Modification,
  type ModificationsMap,
  type ProductModification,
  type ProductModifierGroup,
  type ProductModifierGroups,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a product modifications map from a list of product modifiers.
 */
export function generateModificationsMap(groups: ProductModifierGroups): ModificationsMap {
  const extractedModifications = groups.flatMap(extendModifications);

  return extractedModifications.reduce(appendModification, new Map());
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extends the modifications of the given modifier group by applying a factory function
 * to each modification within the group.
 */
function extendModifications(modifierGroup: ProductModifierGroup, index: number) {
  const { id, name, displayName, variation, modifications } = modifierGroup;

  const extendModification = extendModificationFactory({
    modificationGroupId: id,
    modificationGroupName: name,
    modificationGroupDisplayName: displayName,
    modificationGroupOrder: index,
    modificationGroupVariation: variation,
  });

  return modifications.map(extendModification);
}

/**
 * Appends a product modification to the given modifications map.
 */
function appendModification(modificationsMap: ModificationsMap, modification: Modification) {
  if (!modification.ingredient) {
    return modificationsMap;
  }

  const ingredientId = modification.ingredient.id;

  modificationsMap.set(ingredientId, modification);

  return modificationsMap;
}

/**
 * Creates a function to extend a product modification with additional properties
 * including modification group ID and name, order, and a default quantity value.
 */
function extendModificationFactory(params: ExtendModificationFactoryParams) {
  const {
    modificationGroupId,
    modificationGroupName,
    modificationGroupDisplayName,
    modificationGroupOrder,
    modificationGroupVariation,
  } = params;

  return function (productModification: ProductModification, index: number): Modification {
    return {
      ...productModification,
      modificationGroupId,
      modificationGroupName,
      modificationGroupDisplayName,
      modificationGroupOrder,
      modificationGroupVariation,
      order: index,
      quantity: 1,
    };
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ExtendModificationFactoryParams = {
  modificationGroupId: string;
  modificationGroupName: string;
  modificationGroupDisplayName: string;
  modificationGroupOrder: number;
  modificationGroupVariation: ModifierGroupVariation;
};
